import { FunctionRegistry, flattenArgs, toNum, toStr } from './index';
import { FormulaValue, FormulaError, FormulaErrorType } from '../types';
import { smartRound } from '../evaluator';

// ---------------------------------------------------------------------------
// Registration
// ---------------------------------------------------------------------------

export function registerMatrixFunctions(registry: FunctionRegistry): void {
    // MMULT(array1, array2)
    // For two 1-D arrays of equal length, returns their dot product (scalar).
    // This matches the typical use-case available within this engine's 1-D
    // array model. Full 2-D matrix multiplication is not supported because
    // the engine has no 2-D array type.
    registry.register('MMULT', (...args: FormulaValue[]) => {
        if (args.length < 2) throw new FormulaError(FormulaErrorType.VALUE, 'MMULT: requires 2 arguments');

        const a = Array.isArray(args[0]) ? args[0] : [toNum(args[0])];
        const b = Array.isArray(args[1]) ? args[1] : [toNum(args[1])];

        if (a.length !== b.length) {
            throw new FormulaError(
                FormulaErrorType.VALUE,
                `MMULT: arrays must have equal length (got ${a.length} and ${b.length})`
            );
        }

        if (a.length === 0) throw new FormulaError(FormulaErrorType.VALUE, 'MMULT: arrays must not be empty');

        let dot = 0;
        for (let i = 0; i < a.length; i++) {
            dot += a[i] * b[i];
        }

        return smartRound(dot);
    });

    // TRANSPOSE(array)
    // The transpose of a 1-D row vector is a 1-D column vector, which has
    // the same elements. In this engine's flat array model the result is
    // identical to the input, so we return the array as-is.
    registry.register('TRANSPOSE', (...args: FormulaValue[]) => {
        if (args.length === 0) throw new FormulaError(FormulaErrorType.VALUE, 'TRANSPOSE: requires 1 argument');

        const input = args[0];

        if (Array.isArray(input)) {
            // Return a shallow copy so callers cannot mutate the original
            return [...input];
        }

        // Scalar: wrap in array for consistency with array-returning functions
        return [toNum(input)];
    });
}
